export type TDoctorSpecializationLocaleKey = 'pt-BR' | 'en' | 'es';

export interface IDoctorSpecializationEntry {
  id: number;
  code: string;
  active: boolean;
  names: Partial<Record<TDoctorSpecializationLocaleKey, string>>;
}

const DEFAULT_LOCALE_KEY: TDoctorSpecializationLocaleKey = 'pt-BR';
const LOCALE_KEYS: TDoctorSpecializationLocaleKey[] = ['pt-BR', 'en', 'es'];

function readName(value: unknown): string | undefined {
  return typeof value === 'string' && value.trim() ? value.trim() : undefined;
}

// Validates one raw specialization (Firestore document or cached item). Returns null when the item
// is unusable, so a malformed entry is skipped instead of breaking the whole list.
export function parseDoctorSpecializationEntry(data: unknown): IDoctorSpecializationEntry | null {
  const raw = data as Partial<Record<keyof IDoctorSpecializationEntry, unknown>> | null;

  if (!raw || typeof raw !== 'object') {
    return null;
  }

  const code = typeof raw.code === 'string' ? raw.code.trim() : '';
  const id = Number(raw.id);

  if (!code || raw.id === null || raw.id === '' || !Number.isInteger(id)) {
    return null;
  }

  const rawNames = (raw.names && typeof raw.names === 'object' ? raw.names : {}) as Record<string, unknown>;
  const names: IDoctorSpecializationEntry['names'] = {};

  LOCALE_KEYS.forEach((localeKey) => {
    const name = readName(rawNames[localeKey]);

    if (name) {
      names[localeKey] = name;
    }
  });

  return { id, code, active: raw.active !== false, names };
}

/**
 * Synchronous in-memory store of the doctor specializations configured remotely (Firestore).
 * It is a plain singleton, not an Angular service, because `getDoctorSpecializationLocalization` is a
 * plain function used by view-model factories and templates that have no access to DI.
 * It stays empty until the remote/cached list is loaded, and consumers fall back to the local enum.
 */
class DoctorSpecializationRegistry {
  private entries: IDoctorSpecializationEntry[] = [];
  private localeKeyResolver: () => TDoctorSpecializationLocaleKey = () => DEFAULT_LOCALE_KEY;

  public setEntries(entries: IDoctorSpecializationEntry[]): void {
    this.entries = entries;
  }

  public getEntries(): IDoctorSpecializationEntry[] {
    return this.entries;
  }

  public setLocaleKeyResolver(resolver: () => TDoctorSpecializationLocaleKey): void {
    this.localeKeyResolver = resolver;
  }

  public getLocaleKey(): TDoctorSpecializationLocaleKey {
    return this.localeKeyResolver();
  }

  // Null when no remote list is available, so the caller uses the local fallback list.
  public getActiveEntries(): IDoctorSpecializationEntry[] | null {
    return this.entries.length ? this.entries.filter((entry) => entry.active) : null;
  }

  public isDeactivated(codeOrId: string | number): boolean {
    const entry = this.findEntry(codeOrId);

    return !!entry && !entry.active;
  }

  // Current language -> pt-BR. Deactivated entries keep resolving, so existing records still show a name.
  public getName(codeOrId: string | number): string | undefined {
    const entry = this.findEntry(codeOrId);

    return entry ? readName(entry.names[this.getLocaleKey()]) ?? readName(entry.names[DEFAULT_LOCALE_KEY]) : undefined;
  }

  private findEntry(codeOrId: string | number): IDoctorSpecializationEntry | undefined {
    const value = String(codeOrId);

    return (
      this.entries.find((entry) => entry.code === value) ?? this.entries.find((entry) => String(entry.id) === value)
    );
  }
}

export const doctorSpecializationRegistry = new DoctorSpecializationRegistry();
